import { useState } from 'react';
import {
  Modal,
  Alert,
  Box,
  Button,
  Multiselect,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import {
  MemberTaskSummary,
  RemoveTasksFromGroupCommand,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { callApi } from '../client';
import { AsyncErrorBar } from '../components/AsyncErrorBar';

interface RemoveTasksFromGroupModalProps {
  visible: boolean;
  groupId: string;
  groupName: string;
  memberTaskSummaries: MemberTaskSummary[];
  onDismiss: () => void;
  onSuccess: (groupDeleted: boolean) => void;
}

export const RemoveTasksFromGroupModal = ({
  visible,
  groupId,
  groupName,
  memberTaskSummaries,
  onDismiss,
  onSuccess,
}: RemoveTasksFromGroupModalProps) => {
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthState();

  const resetForm = () => {
    setSelectedOptions([]);
    setError(null);
  };

  const handleCancel = () => {
    resetForm();
    onDismiss();
  };

  const options = memberTaskSummaries.map((t) => ({
    label: t.name,
    value: t.launchId,
    description: t.launchId,
  }));

  const handleSubmit = async () => {
    if (!token || selectedOptions.length === 0) return;
    setLoading(true);
    setError(null);

    const launchIds = selectedOptions.map((o) => o.value);

    try {
      const response = await callApi(
        new RemoveTasksFromGroupCommand({ groupId, launchIds }),
        token
      );
      const groupDeleted = !response.group;
      onSuccess(groupDeleted);
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header='Remove tasks from group'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              loading={loading}
              disabled={selectedOptions.length === 0}
              data-test-id='confirm-remove-tasks-button'
            >
              Remove
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size='m'>
        <AsyncErrorBar error={error} header='Remove Tasks Failed' />
        <Alert type='warning'>
          Removing a task from a group will reset it to 'Task Received' status
          and clear its doc packages. This action cannot be undone.
        </Alert>
        <FormFieldLabel label='Group' value={groupName} />
        <Multiselect
          selectedOptions={selectedOptions}
          onChange={({ detail }) =>
            setSelectedOptions([...detail.selectedOptions])
          }
          options={options}
          placeholder='Select tasks to remove'
          filteringType='auto'
          data-test-id='remove-tasks-multiselect'
        />
      </SpaceBetween>
    </Modal>
  );
};

const FormFieldLabel = ({ label, value }: { label: string; value: string }) => (
  <div>
    <Box variant='awsui-key-label'>{label}</Box>
    <Box>{value}</Box>
  </div>
);
